import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, X, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { supabase } from '../../../services/supabaseClient';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchInitialData();

    // Fermer le menu si on clique en dehors
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInitialData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUser(user);
      
      // 1. On charge l'historique non lu
      const { data } = await supabase
        .from('habitat_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
        
      if (data) setNotifications(data);

      // 2. ⚡ LA MAGIE DU TEMPS RÉEL (Supabase Realtime)
      const channel = supabase
        .channel('realtime_notifications')
        .on(
          'postgres_changes',
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'habitat_notifications',
            filter: `user_id=eq.${user.id}` // On n'écoute que NOS alertes
          },
          (payload) => {
            // Jouer un petit son "Pop" optionnel ici
            setNotifications((prev) => [payload.new, ...prev]);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  };

  const markAsRead = async (id: string) => {
    // Mise à jour visuelle instantanée
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    // Mise à jour en base de données
    await supabase.from('habitat_notifications').update({ is_read: true }).eq('id', id);
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    if (currentUser) {
      await supabase.from('habitat_notifications').update({ is_read: true }).eq('user_id', currentUser.id);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 🔔 L'ICÔNE DE LA CLOCHE */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
      >
        <Bell size={22} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full border-2 border-[#0B1120] animate-in zoom-in">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* 📜 LE MENU DÉROULANT */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 md:w-96 bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden z-[100] animate-in fade-in slide-in-from-top-4">
          <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-black text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="text-[10px] font-bold text-teal-600 hover:text-teal-700 uppercase tracking-widest flex items-center gap-1">
                <Check size={12}/> Tout marquer comme lu
              </button>
            )}
          </div>
          
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Bell size={32} className="mx-auto mb-3 opacity-20" />
                <p className="font-bold text-sm">Aucune notification</p>
                <p className="text-xs mt-1">Vous êtes à jour.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {notifications.map(notif => (
                  <div 
                    key={notif.id} 
                    onClick={() => !notif.is_read && markAsRead(notif.id)}
                    className={`p-4 flex gap-4 cursor-pointer transition-colors ${notif.is_read ? 'opacity-60 bg-white hover:bg-slate-50' : 'bg-teal-50/30 hover:bg-teal-50'}`}
                  >
                    <div className="shrink-0 mt-1">
                      {notif.type === 'success' ? <CheckCircle size={18} className="text-green-500"/> :
                       notif.type === 'warning' ? <AlertTriangle size={18} className="text-amber-500"/> :
                       <Info size={18} className="text-blue-500"/>}
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-sm ${notif.is_read ? 'font-bold text-slate-700' : 'font-black text-slate-900'}`}>{notif.title}</h4>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{notif.message}</p>
                      <p className="text-[9px] text-slate-400 mt-2 uppercase font-bold tracking-widest">{new Date(notif.created_at).toLocaleDateString()} - {new Date(notif.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                    </div>
                    {!notif.is_read && <div className="w-2 h-2 rounded-full bg-teal-500 shrink-0 mt-1"></div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
